import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Header";
import { LogOut, RefreshCw, Trash2, Fuel, Clock, CheckCircle2, Mail, MailOpen, Phone, MapPin, Truck, Ban, PackageCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  received: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  confirmed: "bg-blue-500/15 text-blue-400 border-blue-500/40",
  en_route: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  delivered: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/40",
};

const STATUS_LABELS = { received: "Received", confirmed: "Confirmed", en_route: "En Route", delivered: "Delivered", cancelled: "Cancelled" };

const NEXT_ACTIONS = {
  received: [{ to: "confirmed", label: "Confirm", icon: CheckCircle2 }],
  confirmed: [{ to: "en_route", label: "Dispatch", icon: Truck }],
  en_route: [{ to: "delivered", label: "Mark Delivered", icon: PackageCheck }],
  delivered: [],
  cancelled: [],
};

const fmt = (n) => "\u20A6" + Number(n).toLocaleString("en-NG");
const fmtDate = (iso) => new Date(iso).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("motaps_admin_token") || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem("motaps_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(false);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, authHeaders())
      .then(({ data }) => setUser(data))
      .catch(() => { localStorage.removeItem("motaps_admin_token"); setToken(""); })
      .finally(() => setChecking(false));
  }, [token, authHeaders]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [o, e] = await Promise.all([
        axios.get(`${API}/admin/orders`, authHeaders()),
        axios.get(`${API}/admin/enquiries`, authHeaders()),
      ]);
      setOrders(o.data);
      setEnquiries(e.data);
    } catch {
      toast.error("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [user, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("motaps_admin_token", data.token);
      setToken(data.token);
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("motaps_admin_token");
    setToken("");
    setUser(null);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, authHeaders());
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Order marked as ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("Could not update order status.");
    }
  };

  const deleteOrder = async (id) => {
    try {
      await axios.delete(`${API}/admin/orders/${id}`, authHeaders());
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success("Order deleted.");
    } catch {
      toast.error("Could not delete order.");
    }
  };

  const toggleRead = async (id, read) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { read }, authHeaders());
      setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, read } : x)));
    } catch {
      toast.error("Could not update enquiry.");
    }
  };

  const deleteEnquiry = async (id) => {
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, authHeaders());
      setEnquiries((prev) => prev.filter((x) => x.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0B132B] flex items-center justify-center" data-testid="admin-loading">
        <p className="text-slate-400 font-mono-stat text-sm">Loading dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B132B] flex items-center justify-center px-5 grain-overlay relative" data-testid="admin-login-page">
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          onSubmit={login}
          className="w-full max-w-md bg-[#1C2541]/80 border border-slate-700/50 rounded-3xl p-8 md:p-10 backdrop-blur-md"
          data-testid="admin-login-form"
        >
          <div className="flex justify-center mb-6"><Logo /></div>
          <h1 className="font-display font-extrabold text-2xl text-white text-center" data-testid="admin-login-heading">Staff Dashboard</h1>
          <p className="text-sm text-slate-400 text-center mt-2 mb-8">Sign in to manage diesel orders and enquiries.</p>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
              <Input id="admin-email" type="email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@motapsglobal.com" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
              <Input id="admin-password" type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="bg-[#0B132B] border-slate-700 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-600" />
            </div>
            <Button type="submit" disabled={loggingIn} data-testid="admin-login-btn" className="w-full bg-amber-600 hover:bg-amber-500 text-white rounded-full h-12 font-semibold transition-all duration-200 disabled:opacity-60">
              {loggingIn ? "Signing in..." : "Sign In"}
            </Button>
            <a href="/" data-testid="admin-back-home-link" className="block text-center text-xs text-slate-500 hover:text-amber-500 transition-colors">&larr; Back to website</a>
          </div>
        </motion.form>
      </div>
    );
  }

  const pending = orders.filter((o) => o.status === "received").length;
  const delivered = orders.filter((o) => o.status === "delivered").length;
  const totalLitres = orders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + (o.litres || 0), 0);
  const unread = enquiries.filter((x) => !x.read).length;

  const STATS = [
    { icon: Fuel, label: "Total Orders", value: orders.length, id: "total-orders" },
    { icon: Clock, label: "Awaiting Confirmation", value: pending, id: "pending-orders" },
    { icon: PackageCheck, label: "Delivered", value: delivered, id: "delivered-orders" },
    { icon: Truck, label: "Total Litres", value: totalLitres.toLocaleString(), id: "total-litres" },
  ];

  return (
    <div className="min-h-screen bg-[#0B132B] text-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-slate-800 bg-[#0B132B]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-btn" aria-label="Refresh" className="p-2.5 rounded-full border border-slate-700 text-slate-300 hover:text-amber-500 hover:border-amber-600/50 transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-slate-300 hover:text-red-400 border border-slate-700 hover:border-red-500/50 rounded-full px-4 py-2 transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-display font-extrabold text-3xl text-white" data-testid="admin-welcome">Welcome, {user.name}</h1>
          <p className="text-sm text-slate-400 mt-1">Manage door-to-door diesel orders and customer enquiries.</p>
        </motion.div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {STATS.map(({ icon: Icon, label, value, id }, i) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="bg-[#1C2541]/60 border border-slate-700/50 rounded-2xl p-5"
              data-testid={`admin-stat-${id}`}
            >
              <Icon size={18} className="text-amber-500 mb-3" />
              <p className="font-mono-stat text-2xl font-bold text-white">{value}</p>
              <p className="text-xs text-slate-400 mt-1">{label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex gap-2 border-b border-slate-800" data-testid="admin-tabs">
          <button
            onClick={() => setTab("orders")}
            data-testid="admin-tab-orders"
            className={`px-5 py-3 text-sm font-semibold font-display tracking-wide border-b-2 transition-colors ${tab === "orders" ? "border-amber-500 text-amber-500" : "border-transparent text-slate-400 hover:text-slate-200"}`}
          >
            Diesel Orders ({orders.length})
          </button>
          <button
            onClick={() => setTab("enquiries")}
            data-testid="admin-tab-enquiries"
            className={`px-5 py-3 text-sm font-semibold font-display tracking-wide border-b-2 transition-colors ${tab === "enquiries" ? "border-amber-500 text-amber-500" : "border-transparent text-slate-400 hover:text-slate-200"}`}
          >
            Enquiries ({enquiries.length}){unread > 0 && <span className="ml-2 bg-amber-600 text-white text-[10px] rounded-full px-2 py-0.5">{unread} new</span>}
          </button>
        </div>

        {tab === "orders" && (
          <div className="mt-8 grid gap-5" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="text-slate-500 text-sm py-10 text-center" data-testid="admin-orders-empty">No diesel orders yet.</p>}
            {orders.map((o) => (
              <motion.div
                key={o.id}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-[#1C2541]/60 border border-slate-700/50 rounded-2xl p-6"
                data-testid={`admin-order-${o.order_ref}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono-stat font-bold text-amber-500" data-testid={`order-ref-${o.order_ref}`}>{o.order_ref}</span>
                      <span className={`text-[11px] font-semibold uppercase tracking-wider border rounded-full px-3 py-1 ${STATUS_STYLES[o.status] || STATUS_STYLES.received}`} data-testid={`order-status-${o.order_ref}`}>
                        {STATUS_LABELS[o.status] || o.status}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-white mt-2">{o.name}</h3>
                    <div className="mt-2 space-y-1 text-sm text-slate-400">
                      <a href={`tel:${o.phone.replace(/\s+/g, "")}`} className="flex items-center gap-2 hover:text-amber-500 transition-colors"><Phone size={13} className="text-amber-600" /> {o.phone}</a>
                      <p className="flex items-start gap-2"><MapPin size={13} className="text-amber-600 mt-1 shrink-0" /> {o.address}, {o.state}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono-stat text-xl font-bold text-white">{(o.litres || 0).toLocaleString()} L</p>
                    <p className="text-xs text-slate-500 mt-0.5">Est. {fmt(o.estimated_total || 0)}</p>
                    <p className="text-xs text-slate-500 mt-1">{fmtDate(o.created_at)}</p>
                    {o.delivery_date && <p className="text-xs text-amber-500/80 mt-1">Preferred: {o.delivery_date}</p>}
                  </div>
                </div>
                {o.notes && <p className="mt-3 text-xs text-slate-500 bg-[#0B132B] rounded-lg px-3 py-2">Notes: {o.notes}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {(NEXT_ACTIONS[o.status] || []).map(({ to, label, icon: Icon }) => (
                    <button
                      key={to}
                      onClick={() => setStatus(o.id, to)}
                      data-testid={`order-action-${to}-${o.order_ref}`}
                      className="flex items-center gap-1.5 text-xs font-semibold bg-amber-600/15 border border-amber-600/40 text-amber-400 rounded-full px-4 py-2 hover:bg-amber-600/25 transition-colors"
                    >
                      <Icon size={13} /> {label}
                    </button>
                  ))}
                  {o.status !== "cancelled" && o.status !== "delivered" && (
                    <button onClick={() => setStatus(o.id, "cancelled")} data-testid={`order-action-cancelled-${o.order_ref}`} className="flex items-center gap-1.5 text-xs font-semibold bg-red-500/10 border border-red-500/30 text-red-400 rounded-full px-4 py-2 hover:bg-red-500/20 transition-colors">
                      <Ban size={13} /> Cancel
                    </button>
                  )}
                  <button onClick={() => deleteOrder(o.id)} data-testid={`order-delete-${o.order_ref}`} aria-label="Delete order" className="ml-auto p-2 text-slate-500 hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-8 grid gap-5" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && <p className="text-slate-500 text-sm py-10 text-center" data-testid="admin-enquiries-empty">No enquiries yet.</p>}
            {enquiries.map((x) => (
              <motion.div
                key={x.id}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className={`bg-[#1C2541]/60 border rounded-2xl p-6 ${x.read ? "border-slate-700/50 opacity-70" : "border-amber-600/40"}`}
                data-testid={`admin-enquiry-${x.id.slice(0, 8)}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-bold text-lg text-white">{x.name}</h3>
                    <a href={`tel:${x.phone.replace(/\s+/g, "")}`} className="mt-1 flex items-center gap-2 text-sm text-slate-400 hover:text-amber-500 transition-colors"><Phone size={13} className="text-amber-600" /> {x.phone}</a>
                    <p className="mt-3 text-sm text-slate-300 leading-relaxed">{x.message}</p>
                    <p className="mt-2 text-xs text-slate-500">{fmtDate(x.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleRead(x.id, !x.read)}
                      data-testid={`enquiry-toggle-read-${x.id.slice(0, 8)}`}
                      className="flex items-center gap-1.5 text-xs font-semibold bg-slate-500/10 border border-slate-600/40 text-slate-300 rounded-full px-4 py-2 hover:border-amber-600/40 hover:text-amber-400 transition-colors"
                    >
                      {x.read ? <><Mail size={13} /> Mark Unread</> : <><MailOpen size={13} /> Mark Read</>}
                    </button>
                    <button onClick={() => deleteEnquiry(x.id)} data-testid={`enquiry-delete-${x.id.slice(0, 8)}`} aria-label="Delete enquiry" className="p-2 text-slate-500 hover:text-red-400 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
